// Libs
import classNames from 'classnames/bind';
import { Box } from '@mui/material';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
// Components, Layouts, Pages
import {
  BaseInputSearch,
  BaseTable,
  BaseTableOption,
  BaseToolbar,
  ConfirmModal,
  Loading,
  UnderDevelopment,
} from '~/components/common';
import PageShell from '~/pages/common/pageShell/PageShell';
// Others
import { deletePriceBook, getPriceBookList } from '~/thunks/priceBook/priceBookThunk';
import { priceBookActions, selectIsRefreshPriceBookList } from '~/thunks/priceBook/priceBookSlice';
import useDebounce from '~/utils/hooks/useDebounce';
import { IPaginationResponse } from '~/utils/interface/base';
import { IHeaderCellType, IQueryParamBase } from '~/utils/interface/common';
import { IPriceBook } from '~/utils/interface/priceBook';
import {
  BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_LIMIT_PAGE,
  DEFAULT_NUMBER_ONE,
  DEFAULT_NUMBER_ZERO,
  EMPTY_STRING,
} from '~/utils/constants/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { StorageEnum } from '~/utils/enum/base';
import { RoleEnum, SearchByFilterContentEnum } from '~/utils/enum/common';
import { adminRouteAbsolute } from '~/utils/routes/admin';
import { staffRouteAbsolute } from '~/utils/routes/staff';
import { mockPriceBooks } from '~/utils/mockData/priceBook';
// Styles, images, icons
import styles from './PriceBook.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const columns = (
  t: TFunction,
  onView: (id: string) => void,
  onEdit: (id: string) => void,
  onDelete: (id: string) => void
): IHeaderCellType<IPriceBook>[] => {
  return [
    {
      id: 'name',
      label: t('table_price_book_title_name'),
      render: (record: IPriceBook) => record?.name || EMPTY_STRING,
      minWidth: 150,
    },
    {
      id: 'totalService',
      label: t('table_price_book_title_services'),
      render: (record: IPriceBook) => record?.totalService || DEFAULT_NUMBER_ZERO,
    },
    {
      id: 'totalMaterial',
      label: t('table_price_book_title_materials'),
      render: (record: IPriceBook) => record.totalMaterial || DEFAULT_NUMBER_ZERO,
    },
    {
      id: 'action',
      label: t('table_price_book_title_action'),
      align: 'left',
      render: (record: IPriceBook) => {
        return (
          <BaseTableOption
            onView={() => onView(record.id)}
            onEdit={() => onEdit(record.id)}
            onDelete={() => onDelete(record.id)}
          />
        );
      },
    },
  ];
};

const PriceBook = (props: Props) => {
  //#region Destructuring Props

  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/priceBook']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
  const pageSelected = useMemo<number>(
    () => Number(params?.page ?? BASE_PAGINATION_DEFAULT_CURRENT_PAGE),
    [params?.page]
  );
  const limitPage = useMemo<number>(() => Number(params?.limit ?? DEFAULT_LIMIT_PAGE), [params?.limit]);
  const textSearchParams = useMemo<string>(
    () => String(params?.textSearch || EMPTY_STRING),
    [params?.textSearch]
  );

  const currentRole: string = localStorage.getItem(StorageEnum.ROLE) ?? EMPTY_STRING;
  //#endregion Declare Hook

  //#region Selector
  const isRefreshPriceBookList = useSelector(selectIsRefreshPriceBookList);
  //#endregion Selector

  //#region Declare State
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);

  const [paramObject, setParamObject] = useState<IQueryParamBase>({
    page: pageSelected,
    limit: limitPage,
  });
  const [searchKey, setSearchKey] = useState<string>(EMPTY_STRING);
  const debounceSearchKey = useDebounce(searchKey, DEFAULT_DEBOUNCE_DELAY_TIME);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [pagination, setPagination] = useState<IPaginationResponse>();

  const [totalItems, setTotalItems] = useState<string>(String(mockPriceBooks.length));
  const [priceBookList, setPriceBookList] = useState<IPriceBook[]>(mockPriceBooks);

  const [isConfirmDeletePriceBook, setIsConfirmDeletePriceBook] = useState<boolean>(false);
  const [priceBookSelected, setPriceBookSelected] = useState<string>(EMPTY_STRING);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetPriceBookList(paramObject);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramObject]);

  useEffect(() => {
    if (!isRefreshPriceBookList) return;

    handleGetPriceBookList(paramObject);
    dispatch(priceBookActions.setRefreshPriceBookList(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshPriceBookList]);

  useEffect(() => {
    setSearchKey(textSearchParams);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textSearchParams]);

  useEffect(() => {
    if (pagination || debounceSearchKey) {
      setParamObject({
        ...paramObject,
        page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
        limit: limitPage,
        ...(debounceSearchKey ? { textSearch: debounceSearchKey } : { textSearch: undefined }),
      });

      setSearchParams({
        page: String(BASE_PAGINATION_DEFAULT_CURRENT_PAGE),
        limit: String(limitPage),
        ...(debounceSearchKey ? { textSearch: debounceSearchKey } : {}),
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceSearchKey]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetPriceBookList = (params: IQueryParamBase) => {
    setIsLoading(true);

    dispatch(getPriceBookList(params))
      .unwrap()
      .then((res) => {
        const { responses, pagination, totalItems } = res?.data;
        if (!responses) return;

        setPriceBookList(responses);
        setPagination(pagination);
        setTotalItems(totalItems.toString());
      })
      .catch((err) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleAddPriceBook = () => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(adminRouteAbsolute.createPriceBook);
        break;

      case RoleEnum.STAFF:
        navigate(staffRouteAbsolute.createPriceBook);
        break;
    }
  };

  const handleEdit = (id: string) => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(adminRouteAbsolute.updatePriceBook(id));
        break;

      case RoleEnum.STAFF:
        navigate(staffRouteAbsolute.updatePriceBook(id));
        break;
    }
  };

  const handleDelete = (id: string) => {
    if (!id) return;

    setPriceBookSelected(id);
    setIsConfirmDeletePriceBook(true);
  };

  const handleConfirmDeletePriceBook = () => {
    if (!priceBookSelected) return;

    setIsLoading(true);

    dispatch(deletePriceBook(priceBookSelected))
      .unwrap()
      .then((_res) => {
        dispatch(priceBookActions.setRefreshPriceBookList(true));
      })
      .catch((_error) => {})
      .finally(() => {
        handleCloseConfirmDeletePriceBook();
        setIsLoading(false);
      });
  };

  const handleView = (id: string) => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(`${adminRouteAbsolute.priceBooks}/${id}`);
        break;

      case RoleEnum.STAFF:
        navigate(`${staffRouteAbsolute.priceBooks}/${id}`);
        break;
    }
  };

  const handleRowTableClick = (id: string) => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(`${adminRouteAbsolute.priceBooks}/${id}`);
        break;

      case RoleEnum.STAFF:
        navigate(`${staffRouteAbsolute.priceBooks}/${id}`);
        break;
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchKey(value);
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setParamObject({ ...paramObject, page });
    setSearchParams({ ...params, page: String(page), limit: String(limitPage) });
  };

  const handleRowPerPageChange = (rowPerPage: number) => {
    setParamObject({ ...paramObject, page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE, limit: rowPerPage });
    setSearchParams({
      ...params,
      page: String(BASE_PAGINATION_DEFAULT_CURRENT_PAGE),
      limit: String(rowPerPage),
    });
  };

  const handleCloseUnderDevelopment = () => {
    setIsShowUnderDevelopment(false);
  };

  const handleCloseConfirmDeletePriceBook = () => {
    setIsConfirmDeletePriceBook(false);
    setPriceBookSelected(EMPTY_STRING);
  };
  //#endregion Handle Function

  return (
    <PageShell title={t('menu_price_books', { ns: 'common/sidebar' })}>
    <Box component={'div'} className={cx('container')} id='priceBookPage'>
      <BaseToolbar
        title={t('price_book_title_total', { quantity: totalItems })}
        textAction={t('button_add_price_book')}
        onAction={handleAddPriceBook}
      >
        <BaseInputSearch
          searchByContent={SearchByFilterContentEnum.NAME}
          value={searchKey}
          onChange={handleSearchChange}
        />
      </BaseToolbar>

      <Box component={'div'} className={cx('body')}>
        <BaseTable
          currentRowsPerPage={limitPage}
          currentPage={pageSelected}
          count={pagination?.totalPages || DEFAULT_NUMBER_ONE}
          columns={columns(t, handleView, handleEdit, handleDelete)}
          dataSource={priceBookList || []}
          onPageChange={handlePageChange}
          onRowClick={handleRowTableClick}
          onRowsPerPageChange={handleRowPerPageChange}
        />
      </Box>

      {isLoading && <Loading />}

      {isConfirmDeletePriceBook && (
        <ConfirmModal
          type='delete'
          icon={icons.tableIconActionDelete}
          title={t('common_title_confirm_delete', { ns: 'common/base' })}
          textConfirm={t('common_action_delete', { ns: 'common/base' })}
          onClose={handleCloseConfirmDeletePriceBook}
          onConfirm={handleConfirmDeletePriceBook}
        />
      )}

      <UnderDevelopment isOpen={isShowUnderDevelopment} onClose={handleCloseUnderDevelopment} />
    </Box>
    </PageShell>
  );
};

export default PriceBook;
